import axios from 'axios';

// API Configuration
// For local development (computer)
//const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

//for uat
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://cashdesk.devicom.ma/api';



const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: {
    indexes: null, // This ensures arrays are serialized as ?param=1&param=2 instead of ?param[0]=1&param[1]=2
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token && !config.headers['Authorization']) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const code = error.response?.data?.code;
      // Only redirect if it's NOT a login attempt
      if (!error.config?.url?.includes('/login')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        const params = new URLSearchParams();
        if (code === 'DESK_SESSION_ENDED') {
          params.set('reason', 'desk_session_ended');
        }
        window.location.href = '/login' + (params.toString() ? `?${params.toString()}` : '');
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/login', credentials),
  logout: () => api.post('/logout'),
  getCurrentUser: () => api.get('/profile'),
};

// Caisse API
export const caisseAPI = {
  getAll: (params = {}) => api.get('/caisses', { params }),
  getForFilters: (dashboardOnly = false) => api.get('/caisses/filters', { params: dashboardOnly ? { dashboardOnly: true } : {} }),
  getById: (id) => api.get(`/caisses/${id}`),
  create: (data) => api.post('/caisses', data),
  update: (id, data) => api.put(`/caisses/${id}`, data),
  delete: (id) => api.delete(`/caisses/${id}`),
  activate: (id) => api.post(`/caisses/${id}/activate`),
  deactivate: (id) => api.post(`/caisses/${id}/deactivate`),
};

// User API
export const userAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  activate: (id) => api.put(`/users/${id}/activate`),
  deactivate: (id) => api.put(`/users/${id}/deactivate`),
  resetPassword: (id, data) => api.post(`/users/${id}/reset-password`, data),
  resetMfa: (id) => api.post(`/users/${id}/reset-mfa`),
  getForFilters: () => api.get('/users/simple'),
  getForFiltersByCaisseTransactions: (caisseIds) => api.get('/users/simple/by-caisse-transactions', { 
    params: { caisseIds: caisseIds.join(',') } 
  }),
  getAssignableUsers: () => api.get('/users/simple/assignable'),
  getManagerUsers: () => api.get('/users/simple/managers'),
  resetAllMfa: (mfaRequired) => api.post('/users/mfa/reset-all', { mfaRequired }),
};

// Transaction API
export const transactionAPI = {
  getAll: (params, config) => api.get('/transactions', { params, ...config }),
  getById: (id) => api.get(`/transactions/${id}`),
  create: (data) => api.post('/transactions', data),
  update: (id, data) => api.put(`/transactions/${id}`, data),
  delete: (id) => api.delete(`/transactions/${id}`),
  updateDescription: (id, data) => api.put(`/transactions/${id}/description`, data),
  updateVerificationStatus: (id, status, type) => {
    const params = { status };
    if (type) {
      params.type = type;
    }
    return api.put(`/transactions/${id}/verification-status`, null, { params });
  },
  getHistory: (id) => api.get(`/transactions/${id}/history`),
  updateRefs: (id, data) => api.patch(`/transactions/${id}/refs`, data),
  getComments: (id) => api.get(`/transactions/${id}/comments`),
  exportCSV: (params) => api.get('/transactions/export/csv', { params, responseType: 'blob' }),
  exportPDF: (params) => api.get('/transactions/export/pdf', { params, responseType: 'blob' }),
  addComment: (id, data) => api.post(`/transactions/${id}/comments`, data),
  updateComment: (transactionId, commentId, data) => api.put(`/transactions/${transactionId}/comments/${commentId}`, data),
  deleteComment: (transactionId, commentId) => api.delete(`/transactions/${transactionId}/comments/${commentId}`),
  getVersementsNonVerifies: (params) => api.get('/transactions/versements-non-verifies', { params }),
  getRapprochementCandidates: (params) => api.get('/transactions/rapprochement-candidates', { params }),
  getVerificationsCandidates: (params) => api.get('/transactions/verifications-candidates', { params }),
  confirmVersement: (id, data) => api.post(`/transactions/${id}/confirm-versement`, data),
};

// Category API (for unique labels only)
export const categoryAPI = {
  getAll: () => api.get('/categories'),
  getById: (id) => api.get(`/categories/${id}`),
  create: (data) => api.post('/categories', data), // Only { label }
  update: (id, data) => api.put(`/categories/${id}`, data), // Only { label }
  delete: (id) => api.delete(`/categories/${id}`),
};

// CategoryDetail API (for per-caisse customization)
export const categoryDetailAPI = {
  getAll: (params = {}) => api.get('/category-details', { params }),
  getForFilters: (params = {}) => api.get('/category-details/filters', { params }),
  getForFiltersByCaisse: (caisseId) => api.get(`/category-details/caisse/${caisseId}`),
  getById: (id) => api.get(`/category-details/${id}`),
  create: (data) => api.post('/category-details', data),
  createBulk: (data) => api.post('/category-details/bulk-assign', data), // Uses categoryId instead of label
  update: (id, data) => api.put(`/category-details/${id}`, data),
  delete: (id) => api.delete(`/category-details/${id}`),
  activate: (id) => api.post(`/category-details/${id}/activate`),
  deactivate: (id) => api.post(`/category-details/${id}/deactivate`),
};

// Withdrawal Request API
export const withdrawalRequestAPI = {
  getAll: (params) => api.get('/withdrawal-requests', { params }),
  getForAgent: (params) => api.get('/withdrawal-requests/my-Caisse', { params }),
  getById: (id) => api.get(`/withdrawal-requests/${id}`),
  create: (data) => api.post('/withdrawal-requests', data),
  update: (id, data) => api.put(`/withdrawal-requests/${id}`, data),
  delete: (id) => api.delete(`/withdrawal-requests/${id}`),
  confirm: (id) => api.post(`/withdrawal-requests/${id}/confirm`),
  validate: (id) => api.post(`/withdrawal-requests/${id}/validate`),
  cancel: (id) => api.post(`/withdrawal-requests/${id}/cancel`),
};

// Dashboard API
export const dashboardAPI = {
  getSummary: (caisseId) => api.get(`/dashboard/summary${caisseId ? `?caisseId=${caisseId}` : ''}`),
  getCaisseBalances: (caisseIds) => api.get('/dashboard/caisse-balances', { params: { caisseIds } }),
  getCategorySummary: (caisseIds, dateRange) => api.get('/dashboard/category-summary', { params: { caisseIds, ...dateRange } }),
  getRecentTransactions: (caisseIds, limit = 10) => api.get('/dashboard/recent-transactions', { params: { caisseIds, limit } }),
  getCaisseDashboard: (caisseId) => api.get(`/dashboard/Caisse/${caisseId}`),
  getAllCaissesDashboard: (dateRange, caisseIds) => {
    const params = {};
    if (caisseIds && caisseIds.length > 0) {
      params.caisseIds = caisseIds;
    }
    return api.get('/dashboard/all-caisses', { params });
  },
  getCategoriesSummary: (caisseId, fromDate, toDate, sortBy = 'categoryName', sortDirection = 'ASC') => api.get(`/dashboard/Caisse/${caisseId}/categories-summary`, { 
    params: { fromDate, toDate, sortBy, sortDirection } 
  }),
  getMultipleCaissesCategoriesSummary: (caisseIds, fromDate, toDate, sortBy = 'categoryName', sortDirection = 'ASC') => api.get('/dashboard/categories-summary', { 
    params: { caisseIds, fromDate, toDate, sortBy, sortDirection } 
  }),
  getCoffreTransactions: (caisseId, fromDate, toDate, sortBy = 'createdAt', sortDirection = 'DESC') => api.get(`/dashboard/Caisse/${caisseId}/coffre-transactions`, { 
    params: { fromDate, toDate, sortBy, sortDirection } 
  }),
};

// Desk Presence API
export const deskPresenceAPI = {
  getStatus: () => api.get('/desk-presence/my-status'),
  startSession: (caisseId) => api.post('/desk-presence/start', { caisseId }),
  endSession: () => api.post('/desk-presence/end'),
  getActiveSessions: () => api.get('/desk-presence/active-sessions'),
};

// Notification API
export const notificationAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  markAsRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllAsRead: () => api.patch('/notifications/mark-all-read'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
};

export default api;

